import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Briefcase, MapPin, Clock, Building, LogOut, Loader2, Search } from 'lucide-react';
import api from '../lib/api';
import { removeToken, getUser } from '../lib/auth';

export default function RoleSelectionPage() {
  const navigate = useNavigate();
  const user = getUser();
  const [loading, setLoading] = useState(true);
  const [roles, setRoles] = useState([]);
  const [filteredRoles, setFilteredRoles] = useState([]);
  const [filters, setFilters] = useState({
    search: '',
    department: '',
    location: '',
    employment_type: ''
  });

  useEffect(() => {
    fetchRoles();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [filters, roles]);

  const fetchRoles = async () => {
    try {
      const response = await api.get('/api/roles/active');
      setRoles(response.data);
      setFilteredRoles(response.data);
    } catch (error) {
      toast.error('Failed to load roles');
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...roles];

    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      filtered = filtered.filter(
        (role) =>
          role.title.toLowerCase().includes(searchLower) ||
          role.department.toLowerCase().includes(searchLower)
      );
    }

    if (filters.department) {
      filtered = filtered.filter((role) => role.department === filters.department);
    }

    if (filters.location) {
      filtered = filtered.filter((role) =>
        role.location.toLowerCase().includes(filters.location.toLowerCase())
      );
    }

    if (filters.employment_type) {
      filtered = filtered.filter((role) => role.employment_type === filters.employment_type);
    }

    setFilteredRoles(filtered);
  };

  const handleLogout = () => {
    removeToken();
    toast.success('Logged out successfully');
    navigate('/');
  };

  const departments = [...new Set(roles.map((r) => r.department))];
  const employmentTypes = [...new Set(roles.map((r) => r.employment_type))];

  if (loading) {
    return (
      <div className="min-h-screen bg-white/40 flex items-center justify-center">
        <Loader2 className="w-12 h-12 text-teal-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white/40">
      {/* Header */}
      <header className="bg-white border-b border-white/40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-2">
              <div className="w-10 h-10 bg-gradient-to-br from-teal-500 to-teal-600 rounded-xl flex items-center justify-center">
                <span className="text-white font-bold text-xl">G</span>
              </div>
              <div>
                <h1 className="text-xl font-bold text-navy-900">girgit</h1>
                <p className="text-xs text-neutral-600">Explore Roles</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <span className="text-sm text-neutral-600">
                Welcome, <span className="font-semibold text-navy-800">{user?.full_name}</span>
              </span>
              <Link
                to="/dashboard"
                className="text-neutral-700 hover:text-navy-900 font-medium transition-colors"
              >
                My Applications
              </Link>
              <button
                onClick={handleLogout}
                className="flex items-center gap-2 text-neutral-700 hover:text-navy-900 transition-colors"
                data-testid="logout-button"
              >
                <LogOut className="w-5 h-5" />
                Logout
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto py-12 px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-navy-900 mb-2">Open Roles</h1>
          <p className="text-lg text-neutral-600">
            Explore opportunities at Half Eaten Donut and find your perfect fit
          </p>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl shadow-soft border border-white/40 p-6 mb-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-navy-700 mb-2">
                Search
              </label>
              <div className="relative">
                <Search className="absolute left-3 top-3 w-5 h-5 text-neutral-400" />
                <input
                  type="text"
                  value={filters.search}
                  onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                  className="w-full pl-10 pr-4 py-2 border border-white/40 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                  placeholder="Search roles..."
                  data-testid="search-input"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-navy-700 mb-2">
                Department
              </label>
              <select
                value={filters.department}
                onChange={(e) => setFilters({ ...filters, department: e.target.value })}
                className="w-full px-4 py-2 border border-white/40 rounded-xl focus:ring-2 focus:ring-teal-500"
              >
                <option value="">All Departments</option>
                {departments.map((dept) => (
                  <option key={dept} value={dept}>
                    {dept}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-navy-700 mb-2">
                Location
              </label>
              <input
                type="text"
                value={filters.location}
                onChange={(e) => setFilters({ ...filters, location: e.target.value })}
                className="w-full px-4 py-2 border border-white/40 rounded-xl focus:ring-2 focus:ring-teal-500"
                placeholder="e.g., Bangalore"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-navy-700 mb-2">
                Employment Type
              </label>
              <select
                value={filters.employment_type}
                onChange={(e) => setFilters({ ...filters, employment_type: e.target.value })}
                className="w-full px-4 py-2 border border-white/40 rounded-xl focus:ring-2 focus:ring-teal-500"
              >
                <option value="">All Types</option>
                {employmentTypes.map((type) => (
                  <option key={type} value={type}>
                    {type}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Roles Grid */}
        {filteredRoles.length === 0 ? (
          <div className="bg-white rounded-xl shadow-soft border border-white/40 p-12 text-center">
            <Briefcase className="w-16 h-16 text-neutral-400 mx-auto mb-4" />
            <h3 className="text-2xl font-semibold text-navy-800 mb-2">No Roles Found</h3>
            <p className="text-neutral-600">Try adjusting your filters</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredRoles.map((role) => (
              <Link
                key={role._id}
                to={`/roles/${role._id}`}
                className="bg-white rounded-xl shadow-soft border border-white/40 hover:shadow-lg transition-all duration-300 p-6 block"
                data-testid="role-card"
              >
                <div className="mb-4">
                  <h3 className="text-xl font-semibold text-navy-900 mb-2">
                    {role.title}
                  </h3>
                  <p className="text-sm text-neutral-600 line-clamp-2">
                    {role.about_role}
                  </p>
                </div>

                <div className="space-y-2 text-sm text-neutral-600 mb-4">
                  <div className="flex items-center">
                    <Building className="w-4 h-4 mr-2 text-teal-600" />
                    {role.department}
                  </div>
                  <div className="flex items-center">
                    <MapPin className="w-4 h-4 mr-2 text-teal-600" />
                    {role.location}
                  </div>
                  <div className="flex items-center">
                    <Clock className="w-4 h-4 mr-2 text-teal-600" />
                    {role.employment_type}
                  </div>
                  <div className="flex items-center">
                    <Briefcase className="w-4 h-4 mr-2 text-teal-600" />
                    {role.experience_min}-{role.experience_max} years
                  </div>
                </div>

                <div className="pt-4 border-t border-white/40">
                  <p className="text-sm text-teal-600 font-medium hover:text-teal-700">
                    View Details & Apply →
                  </p>
                </div>
              </Link>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}